import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for location-aware listing pages (DogParks, Services, Events, Organizations)
 * Consolidates duplicated location-fetching logic across multiple components
 * 
 * @param {Object} options Configuration options
 * @param {string} options.endpoint - API endpoint (e.g., '/api/parks', '/api/services')
 * @param {Function} options.buildParams - Function to build additional query params from filters
 * @param {Object} options.initialFilters - Initial filter values
 * @param {boolean} options.autoFetch - Whether to auto-fetch on mount (default: true)
 * @returns {Object} Hook state and methods
 */
export const useLocationListing = ({
  endpoint,
  buildParams = () => ({}),
  initialFilters = {},
  autoFetch = true
}) => {
  // Data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Location state
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  // Filter state
  const [sortBy, setSortBy] = useState('rating');
  const [maxDistance, setMaxDistance] = useState('any');
  const [filters, setFilters] = useState(initialFilters);

  // Try to get cached location from localStorage on mount
  useEffect(() => {
    const cached = localStorage.getItem('userLocation');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // Only use if less than 1 hour old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 3600000) {
          setUserLocation({ lat: parsed.lat, lng: parsed.lng });
          setSortBy('distance'); // Auto-sort by distance when location is cached
        }
      } catch (e) {
        localStorage.removeItem('userLocation');
      }
    }
  }, []);

  // Request user's location
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      const error = 'Geolocation is not supported by your browser';
      setLocationError(error);
      toast.error(error);
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(newLocation);
        setSortBy('distance');
        setLocationLoading(false);
        
        // Cache location
        localStorage.setItem('userLocation', JSON.stringify({
          ...newLocation,
          timestamp: Date.now()
        }));
        
        toast.success('Location found! Sorting by distance.');
      },
      (err) => {
        setLocationError(err.message);
        setLocationLoading(false);
        toast.error('Unable to get your location. Please check your browser settings.');
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  }, []);

  // Clear location
  const clearLocation = useCallback(() => {
    setUserLocation(null);
    setSortBy('rating');
    setMaxDistance('');
    localStorage.removeItem('userLocation');
  }, []);

  // Fetch items from API
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...buildParams(filters),
      };
      
      // Add location params if available
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
        params.sort_by = sortBy;
        if (maxDistance && maxDistance !== 'any') {
          params.max_distance = parseFloat(maxDistance);
        }
      }
      
      const response = await axios.get(`${API}${endpoint}`, { params });
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [endpoint, filters, userLocation, sortBy, maxDistance, buildParams]);

  // Fetch when dependencies change
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Update a single filter
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setMaxDistance('');
  }, [initialFilters]);

  return {
    // Data
    items,
    loading,
    
    // Location
    userLocation,
    locationLoading,
    locationError,
    hasLocation: !!userLocation,
    requestLocation,
    clearLocation,
    
    // Sorting & Filtering
    sortBy,
    setSortBy,
    maxDistance,
    setMaxDistance,
    filters,
    setFilters,
    updateFilter,
    resetFilters,
    
    // Actions
    refetch: fetchItems
  };
};

/**
 * Distance filter dropdown options (commonly used values)
 */
export const DISTANCE_OPTIONS = [
  { value: '', label: 'Any Distance' },
  { value: '5', label: 'Within 5 miles' },
  { value: '10', label: 'Within 10 miles' },
  { value: '25', label: 'Within 25 miles' },
  { value: '50', label: 'Within 50 miles' },
  { value: '100', label: 'Within 100 miles' },
];

/**
 * Helper component for location controls
 * Can be used in any listing page
 */
export const LocationControls = ({
  userLocation,
  locationLoading,
  requestLocation,
  maxDistance,
  setMaxDistance,
  clearLocation
}) => {
  return {
    hasLocation: !!userLocation,
    onRequestLocation: requestLocation,
    onClearLocation: clearLocation,
    locationLoading,
    maxDistance,
    onMaxDistanceChange: setMaxDistance,
    distanceOptions: DISTANCE_OPTIONS
  };
};

export default useLocationListing;
